import {
  computed,
  createLogger,
  defineStore,
  ref,
  useAuth,
  useNuxtApp,
  useSupabase,
  useBookmarks,
  useFollowers,
  watch,
} from "#imports";
import * as Sentry from "@sentry/nuxt";
import { isSSR } from "~/constants";
import { getIp } from "./utils";

export type UserTier = "premium" | "supporter";
export type UserPaymentInterval = "month" | "year";

export const useUser = defineStore("user", () => {
  const logger = createLogger("stores/user");
  const auth = useAuth();
  const supabase = useSupabase();

  /** @private */
  const userInfo = ref<null | Awaited<ReturnType<typeof fetchUserInfo>>>(null);
  const fetchUserInfo = async (forceRefetch: boolean = false) => {
    if (!auth.authUser) {
      throw new Error("Need to authenticate before fetching user info");
    }

    if (!forceRefetch && userInfo.value) {
      return;
    }

    if (!auth.authUser) {
      throw new Error("Need to authenticate before fetching user info");
    }

    const stopTimer = logger.time("fetchUserInfo");
    const { data } = await supabase
      .from("profiles") // really don't get why is_admin doesn't get typed from the select *
      .select(
        `*, is_admin,
        terms_audit_log (id, user_id),
        privacy_policy_agreement_audit_log(id, user_id, terms_version),
        platform_subscriptions (*),
        supports:creator_supporters!user_id (*)`,
      )
      .eq("id", auth.authUser.sub)
      .maybeSingle()
      .throwOnError();

    userInfo.value = data;
    stopTimer();
    return data;
  };

  /** users profile info, bookmarks, active subscriptions and active supports */
  const user = computed(() => ({
    ...userInfo.value,
    // client side supabase-js is updated beyond this field being here, but it is, be aware if it vanishes
    email: auth.authUser?.email,
    // supabase doesn't support extra filtering with joins (ON), so we do it here
    subscriptions:
      userInfo.value?.platform_subscriptions.filter(
        (subscription) => subscription.status === "active",
      ) ?? [],
    supports:
      userInfo.value?.supports.filter(
        (supported) => supported.status === "active",
      ) ?? [],
  }));

  const isAdmin = computed(() => userInfo.value?.is_admin);

  const hasApplied = computed(
    () => (userInfo.value?.terms_audit_log?.length ?? 0) > 0,
  );

  const isSupporter = (creator_id: string) =>
    computed(() => {
      return (
        (user.value?.supports.filter(
          (supported) => supported.creator_id === creator_id,
        ).length ?? 0) > 0
      );
    });

  const isPremium = computed(
    () =>
      (user.value?.subscriptions.length ?? 0) > 0 ||
      (user.value?.supports.length ?? 0) > 0,
  );

  const submitPrivacyPolicy = async () => {
    if (!userInfo.value?.id) {
      return false;
    }

    await supabase
      .from("privacy_policy_agreement_audit_log")
      .insert({
        user_id: userInfo.value.id,
        terms_version: "1.0",
        ip: (await getIp()) ?? "Could not get IP address",
      })
      .throwOnError();

    await fetchUserInfo(true);
  };

  const hasSignedPrivacyPolicy = computed(
    () => (userInfo.value?.privacy_policy_agreement_audit_log?.length ?? 0) > 0,
  );

  const fetchStripeSessionUrl = async () => {
    const { data, error } = await supabase.functions.invoke<string>(
      "payments/customer-portal",
      {
        method: "GET",
      },
    );
    if (error) throw error;
    if (!data) throw new Error("Failed to load stripe session url");
    return data;
  };

  watch(
    () => auth.isLoggedIn,
    async () => {
      if (!auth.isLoggedIn || !auth.authUser) {
        // clear vars
        userInfo.value = null;
      } else {
        await fetchUserInfo();
        await useFollowers().fetchFollowers();
        await useBookmarks().fetchMapBookmarks();
        await useBookmarks().fetchSpotBookmarks();

        // setup posthog and sentry for the user, skip in server side rendering
        if (isSSR) {
          return;
        }

        const { $posthogClient } = useNuxtApp();
        $posthogClient.identify(auth.authUser.sub, {
          email: auth.authUser.email,
          is_creator: userInfo.value!.is_creator,
          is_subscriber: userInfo.value!.platform_subscriptions?.length > 0,
          is_supporter: userInfo.value!.supports?.length > 0,
        });
        Sentry.setUser({
          id: auth.authUser.sub,
          email: auth.authUser.email,
        });
      }
    },
  );

  return {
    userInfo,
    fetchUserInfo,
    user,
    isPremium,
    isAdmin,
    isSupporter,
    hasApplied,
    submitPrivacyPolicy,
    hasSignedPrivacyPolicy,
    fetchStripeSessionUrl,
  };
});
